<script setup lang="ts">
import Api from "@/services/api/ApiServiceFabrick"
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
import createOrUpdateSubManagementOffice from "@/types/requests/createOrUpdateSubManagementOffice"
import TextField from "@/components/Common/FormTextField.vue"
import TextArea from "@/components/Common/FormTextArea.vue"
import AddressForm from "@/components/SubManagementOfficeForm/AddressForm.vue"
import TelephoneNumberForm from "@/components/SubManagementOfficeForm/TelephoneNumberForm.vue"
import { useAuth } from "@/services/user/Auth0UserProvider"
import { useRoute, useRouter } from 'vue-router'
import { Ref, ref, onBeforeUnmount } from 'vue'
import {scrollToErrorComponent} from "@/services/validators"
import i18n from '@/lang/i18n'

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any },
  switchContentLoading: (bool: boolean) => { value: any }
}>()

const { loginWithRedirect } = useAuth()
const router = useRouter()
const route = useRoute()
const title: Ref<string> = ref('')
const showableUpdateButton: Ref<boolean> = ref(false)
const errors: any = ref({})
const subManagementOffice = ref({} as createOrUpdateSubManagementOffice)
const isUpdating: Ref<boolean> = ref(false)
const subManagementOfficeId: Ref<number | null> = ref(null)
const windowInnerWidth: Ref<number> = ref(window.innerWidth);

(async () => {
  props.switchContentLoading(true)
  if (route.params.id) subManagementOfficeId.value = Number(route.params.id)
  const titlePrefix = i18n.global.t('pages.subManagementOffice.title.listTitle')
  const titleSuffix = subManagementOfficeId.value ? i18n.global.t('pages.subManagementOffice.title.editTitle') : i18n.global.t('pages.subManagementOffice.title.createTitle')
  title.value = `${titlePrefix}/${titleSuffix}`
  document.title = title.value

  AuthorityCacheApi.getWithParse().then((authorityList) => {
    if (subManagementOfficeId.value) {
      showableUpdateButton.value = !!authorityList["octlink.submanagement_office.update"]
    } else {
      showableUpdateButton.value = !!authorityList["octlink.submanagement_office.create"]
    }
    if (!authorityList["octlink.submanagement_office.read"]) {
      router.push('/')
    }
  })

  if (subManagementOfficeId.value) {
    const response = await Api().getSubManagementOffice(subManagementOfficeId.value)
      .catch((error) => {
        if (error.request.status == 403) {
          props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), "error", true)
          router.push('/sub-management-offices/')
        }
        return errorHandle(error.request)
      })
    if(response) {
      subManagementOffice.value = response
      if (!subManagementOffice.value.zipcode) addInitialValue()
      subManagementOffice.value.ip_keeps_effective = 0
    }
  }
  props.switchContentLoading(false)
})()

function addInitialValue() {
  subManagementOffice.value.zip1 = ""
  subManagementOffice.value.zip2 = ""
}

function submit () {
  isUpdating.value = true
  const requestParams = makeRequestParams()
  Api().createOrUpdateSubManagementOffice(subManagementOfficeId.value ?? null, requestParams).then(() => {
    success()
  }).catch((error) => {
    errorHandle(error.response)
  })
}

function makeRequestParams (): createOrUpdateSubManagementOffice {
  let zipcode = ""
  if (subManagementOffice.value?.zip1 && subManagementOffice.value?.zip2) {
    zipcode = `${subManagementOffice.value.zip1}-${subManagementOffice.value.zip2}`
  }

  subManagementOffice.value.zipcode = zipcode
  subManagementOffice.value.payment_site = subManagementOffice.value.payment_site ?? 0
  subManagementOffice.value.payment_day = subManagementOffice.value.payment_day ?? 0
  subManagementOffice.value.has_file = subManagementOffice.value?.img ? 1 : 0
  subManagementOffice.value.password_expire_days = subManagementOffice.value?.password_expire_days ?? 0
  subManagementOffice.value.password_expire_days_checkbox = subManagementOffice.value?.password_expire_days ? 1 : 0
  subManagementOffice.value.login_error_limit_times = subManagementOffice.value.login_error_limit_times ?? 0
  subManagementOffice.value.login_error_limit_times_checkbox = subManagementOffice.value?.login_error_limit_times ? 1 : 0
  subManagementOffice.value.ip_keeps_effective = subManagementOffice.value?.ip_address?.length ? 1 : 0
  subManagementOffice.value.ip_address = subManagementOffice.value.ip_address ?? []

  return subManagementOffice.value
}


function success () {
  isUpdating.value = false
  router.push('/sub-management-offices')
  props.showFlash(i18n.global.t('pages.common.messages.success'), "success", true)
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), "error", true)
      showableUpdateButton.value = false
      break
    case 422:
      errors.value = res.data.errors
      scrollToErrorComponent()
  }
  return isUpdating.value = false
}

function handleResize () {
  windowInnerWidth.value = window.innerWidth
}

window.addEventListener('resize', handleResize)

onBeforeUnmount(() => {
  props.switchContentLoading(false)
})
</script>
<template>
  <div style="min-height: 45vw;">
    <h4 class="content pl-3 w-100">{{title}}</h4>
    <div class="row mr-3 ml-3 v-theme--light">
      <div class="mb-4">
        <TextField
          v-model="subManagementOffice.dks_no_initial"
          :title="i18n.global.t('pages.subManagementOffice.dksNoInitial')"
          :error="errors.dks_no_initial"
          :windowInnerWidth="windowInnerWidth"
        />
        <TextField
          v-model="subManagementOffice.company_name"
          :title="i18n.global.t('pages.subManagementOffice.companyName')"
          :error="errors.company_name"
          :windowInnerWidth="windowInnerWidth"
          :required="true"
        />
        <TextField
          v-model="subManagementOffice.company_name_japan"
          :title="i18n.global.t('pages.subManagementOffice.form.companyNameJapan')"
          :error="errors.company_name_japan"
          :windowInnerWidth="windowInnerWidth"
          :required="true"
        />
        <TextField
          v-model="subManagementOffice.contact_name"
          :title="i18n.global.t('pages.subManagementOffice.contactName')"
          :error="errors.contact_name"
          :windowInnerWidth="windowInnerWidth"
        />
        <AddressForm
          v-model="subManagementOffice"
          :errors="errors"
          :windowInnerWidth="windowInnerWidth"
        />
        <TelephoneNumberForm
          v-model="subManagementOffice"
          :errors="errors"
          :windowInnerWidth="windowInnerWidth"
        />
        <TextField
          v-model="subManagementOffice.email"
          :title="i18n.global.t('pages.subManagementOffice.form.email')"
          :error="errors.email"
          :windowInnerWidth="windowInnerWidth"
        />
        <TextArea
          v-model="subManagementOffice.comment"
          :title="i18n.global.t('pages.subManagementOffice.comment')"
          :error="errors.comment"
          :windowInnerWidth="windowInnerWidth"
        />
      </div>
      <div
        v-if="showableUpdateButton"
        class="form-footer p-2"
      >
        <router-link :to="{ name: 'SubManagementOffices' }">
          <v-btn class="ma-2 reset-btn">
            {{ i18n.global.t('common.cancelBtn') }}
          </v-btn>
        </router-link>
        <v-btn
          class="ma-2 exec-btn"
          @click="submit"
          :loading="isUpdating"
        >
          {{ i18n.global.t('pages.common.button.conservation') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>
